/* How a NOVA verdict is drawn: gauge, scale bar, colour and summary line.
   NOVA runs 1 (unprocessed) to 4 (ultra-processed), so lower is better. */

import { escapeHtml } from "./dom";
import type { NovaGroup, Safety } from "./types";

/** Short name per group, used on pills and the scale bar. */
export const NOVA_SHORT: Record<NovaGroup, string> = {
  1: "Minimally processed",
  2: "Culinary ingredient",
  3: "Processed",
  4: "Ultra-processed",
};

/** CSS colour variable per group. */
export function novaColour(group: NovaGroup): string {
  return { 1: "var(--g-a)", 2: "var(--g-b)", 3: "var(--g-c)", 4: "var(--g-e)" }[group];
}

/** Circular gauge: a fuller arc means less processed. */
export function gauge(group: NovaGroup, size: "lg" | "sm" = "lg"): string {
  const r = 42;
  const arc = 2 * Math.PI * r * 0.75; // a 270° arc
  const fill = arc * ((5 - group) / 4);
  return (
    `<div class="gauge ${size}" style="--tone:${novaColour(group)}" role="img" ` +
    `aria-label="NOVA group ${group} of 4, ${escapeHtml(NOVA_SHORT[group])}">` +
    `<svg viewBox="0 0 100 100" aria-hidden="true">` +
    `<circle class="track" cx="50" cy="50" r="${r}" stroke-dasharray="${arc.toFixed(1)} 999" />` +
    `<circle class="value" cx="50" cy="50" r="${r}" stroke-dasharray="${fill.toFixed(1)} 999" />` +
    `</svg>` +
    `<div class="gauge-text"><small>NOVA</small><b>${group}</b></div>` +
    `</div>`
  );
}

/** Four-step bar with a marker on this product's group. */
export function scaleBar(group: NovaGroup): string {
  const steps = ([4, 3, 2, 1] as NovaGroup[])
    .map((g) => `<span class="${g === group ? "on" : ""}" style="--tone:${novaColour(g)}"></span>`)
    .join("");
  return (
    `<div class="scale"><div class="scale-steps">${steps}</div>` +
    `<div class="scale-labels"><span>Ultra-processed</span><span>Processed</span><span>Minimal</span></div></div>`
  );
}

export function pill(safety: Safety): string {
  return (
    `<span class="pill" style="--tone:${novaColour(safety.nova_group)}">` +
    `${escapeHtml(safety.verdict)} · ${escapeHtml(NOVA_SHORT[safety.nova_group])}</span>`
  );
}

/** CSS colour variable per Siga class: green for A, amber for B, red for C2 and C3. */
export const SIGA_NAMES: Record<string, string> = {
  A0: "Unprocessed",
  A1: "Minimally processed",
  B1: "Processed, nutritionally balanced",
  B2: "Processed, high salt, sugar or fat",
  "C0.1": "Ultra-processed, level 0, balanced",
  "C0.2": "Ultra-processed, level 0, high salt, sugar or fat",
  C1: "Ultra-processed, level 1",
  C2: "Ultra-processed, level 2",
  C3: "Ultra-processed, level 3",
};

export function sigaColour(cls: string): string {
  if (cls.startsWith("A")) return "var(--g-a)";
  if (cls === "B1") return "var(--g-b)";
  if (cls.startsWith("B")) return "var(--g-c)";
  if (cls === "C2" || cls === "C3") return "var(--g-e)";
  return "var(--g-d)";
}

/** Siga class and its 0-100 reading, shown under the NOVA scale. Empty for
 *  results saved before Siga existed. */
export function sigaBox(safety: Safety): string {
  const cls = safety.siga_class;
  if (!cls || safety.siga_score === undefined) return "";
  const score = `${safety.siga_estimated ? "about " : ""}${safety.siga_score}/100`;
  const reasons = [...new Set(safety.siga_reasons ?? [])].slice(0, 3);
  return (
    `<div class="siga-box" style="--tone:${sigaColour(cls)}">` +
    `<span class="siga-class"><small>SIGA</small><b>${escapeHtml(cls)}</b></span>` +
    (safety.grade ? `<span class="grade-badge" aria-label="Grade ${safety.grade}"><small>GRADE</small><b>${safety.grade}</b></span>` : "") +
    `<div class="siga-text"><strong>${escapeHtml(safety.siga_name ?? "")}</strong>` +
    `<span class="muted small">${escapeHtml(score)}${reasons.length ? " · " + escapeHtml(reasons.join(", ")) : ""}</span>` +
    (safety.siga_nutrition_known === false
      ? `<span class="muted small">Salt, sugar and fat unknown, so the class is partial.</span>`
      : "") +
    `</div></div>`
  );
}

/** Lower-case a label for use mid-sentence, but leave codes like "INS 322" alone. */
export function inSentence(label: string): string {
  return /\d/.test(label) ? label : label.toLowerCase();
}

export function listWords(items: string[]): string {
  const words = [...new Set(items.map(inSentence))];
  if (words.length <= 1) return words.join("");
  return words.slice(0, -1).join(", ") + " and " + words[words.length - 1];
}

/** One plain sentence explaining the group. */
export function summarySentence(safety: Safety): string {
  const reasons = [...new Set(safety.nova_reasons)].slice(0, 3);
  switch (safety.nova_group) {
    case 1:
      return "Simple ingredients only: no additives or processing aids declared on the label.";
    case 2:
      return "A processed culinary ingredient, with no additives declared.";
    case 3:
      return reasons.length
        ? `Mostly simple ingredients, with ${listWords(reasons)}. Processed, but not ultra-processed.`
        : "Mostly simple ingredients, with some processed ones.";
    case 4:
      return reasons.length
        ? `Ultra-processed: relies on ${listWords(reasons)} rather than simple ingredients alone.`
        : "Ultra-processed: the label declares additives NOVA links to ultra-processed food.";
  }
}

/** Placeholder pack drawn when there is no photo: a coloured pouch with a label. */
export function packArt(name: string, brand: string, seed: string): string {
  const hues = [14, 28, 42, 95, 160, 350];
  let h = 0;
  for (const c of seed || name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const hue = hues[h % hues.length];
  return (
    `<div class="pack-art" style="--hue:${hue}">` +
    `<div class="pack-label"><small>${escapeHtml(brand || "Food")}</small>` +
    `<b>${escapeHtml(name || "Unnamed product")}</b></div></div>`
  );
}

/** The lines behind the 0-100 value: start, what the class cost, and why. */
export function breakdownList(safety: Safety): string {
  const lines = safety.score_breakdown;
  if (!lines?.length) return "";
  const rows = lines
    .map((l) => {
      const pts = l.points === null ? "" : `<b class="pts ${l.points < 0 ? "neg" : ""}">${l.points > 0 && l.label !== "Starting score" ? "+" : ""}${l.points}</b>`;
      return `<li class="${l.points === null ? "reason" : ""}"><span>${escapeHtml(l.label)}</span>${pts}</li>`;
    })
    .join("");
  return `<ul class="breakdown">${rows}</ul>`;
}
